import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import type { HolderRow, ItemDetailResponse } from '../types';

const PAGE_SIZE = 10;

function formatRelative(dateString?: string) {
  if (!dateString) return 'Unknown';
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.max(1, Math.floor(diff / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function usePaginatedTable(fetcher: (offset: number, limit: number) => Promise<{ results: HolderRow[]; hasMore: boolean }>) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<HolderRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcher((page - 1) * PAGE_SIZE, PAGE_SIZE).then((data) => {
      if (!cancelled) {
        setRows(data.results);
        setHasMore(data.hasMore);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [page]);

  return { page, setPage, rows, hasMore, loading };
}

export function ItemPage() {
  const { itemKey = '' } = useParams();
  const [data, setData] = useState<ItemDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const holders = usePaginatedTable((offset, limit) => api.getItemHolders(itemKey, offset, limit));
  const recent = usePaginatedTable((offset, limit) => api.getItemRecent(itemKey, offset, limit));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await api.getItem(itemKey);
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load item');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [itemKey]);

  if (loading) return <div className="panel empty-state">Loading item details...</div>;
  if (error || !data) return <div className="panel empty-state">{error || 'Item not found'}</div>;

  const { item } = data;

  return (
    <div className="page-stack">
      <div className="detail-breadcrumb">
        <Link to="/" className="text-link">Items</Link>
        <span>/</span>
        <span>{item.name}</span>
      </div>

      <section className="detail-layout">
        <aside className="panel item-hero-card">
          <div className="item-hero-card__media">
            {item.image_url ? <img src={item.image_url} alt={item.name} /> : <span>{item.type === 'Gem' ? '💎' : '🔨'}</span>}
          </div>
          <h1>{item.name}</h1>
          <div className="item-tags">
            <span className="badge">{item.rarity || 'Default'}</span>
            <span className="badge badge--muted">{item.type || 'Unknown'}</span>
            <span className="badge badge--muted">{item.category || 'Base'}</span>
          </div>
          <div className="detail-metrics">
            <div><strong>{item.owners.toLocaleString()}</strong><span>owners</span></div>
            <div><strong>{item.copies.toLocaleString()}</strong><span>copies</span></div>
          </div>
        </aside>

        <div className="page-stack">
          <section className="panel">
            <div className="panel__header">
              <div>
                <p className="eyebrow">Ownership</p>
                <h2>Top holders</h2>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {holders.rows.map((holder, index) => (
                    <tr key={`${holder.roblox_id}-${index}`}>
                      <td>#{(holders.page - 1) * PAGE_SIZE + index + 1}</td>
                      <td>
                        <div className="table-player">
                          {holder.headshot_url
                            ? <img className="table-avatar" src={holder.headshot_url} alt={holder.username} />
                            : <div className="table-avatar table-avatar--placeholder" />}
                          <div>
                            <Link className="table-link" to={`/players/${holder.roblox_id}`}>
                              {holder.display_name || holder.username}
                            </Link>
                            <div className="table-subcopy">@{holder.username}</div>
                          </div>
                        </div>
                      </td>
                      <td>{holder.quantity.toLocaleString()}</td>
                    </tr>
                  ))}
                  {!holders.loading && holders.rows.length === 0 ? (
                    <tr><td colSpan={3} className="table-empty">No ownership data yet.</td></tr>
                  ) : null}
                  {holders.loading ? (
                    <tr><td colSpan={3} className="table-empty">Loading...</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <button className="button" disabled={holders.page <= 1} onClick={() => holders.setPage((p) => p - 1)}>Previous</button>
              <span className="pager__info">Page {holders.page}</span>
              <button className="button" disabled={!holders.hasMore} onClick={() => holders.setPage((p) => p + 1)}>Next</button>
            </div>
          </section>

          <section className="panel">
            <div className="panel__header">
              <div>
                <p className="eyebrow">Recent</p>
                <h2>Latest sightings</h2>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Quantity</th>
                    <th>Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.rows.map((holder, index) => (
                    <tr key={`${holder.roblox_id}-${index}`}>
                      <td>
                        <div className="table-player">
                          {holder.headshot_url
                            ? <img className="table-avatar" src={holder.headshot_url} alt={holder.username} />
                            : <div className="table-avatar table-avatar--placeholder" />}
                          <div>
                            <Link className="table-link" to={`/players/${holder.roblox_id}`}>
                              {holder.display_name || holder.username}
                            </Link>
                            <div className="table-subcopy">@{holder.username}</div>
                          </div>
                        </div>
                      </td>
                      <td>{holder.quantity.toLocaleString()}</td>
                      <td>{formatRelative(holder.updated_at)}</td>
                    </tr>
                  ))}
                  {!recent.loading && recent.rows.length === 0 ? (
                    <tr><td colSpan={3} className="table-empty">No recent sightings yet.</td></tr>
                  ) : null}
                  {recent.loading ? (
                    <tr><td colSpan={3} className="table-empty">Loading...</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <div className="pager">
              <button className="button" disabled={recent.page <= 1} onClick={() => recent.setPage((p) => p - 1)}>Previous</button>
              <span className="pager__info">Page {recent.page}</span>
              <button className="button" disabled={!recent.hasMore} onClick={() => recent.setPage((p) => p + 1)}>Next</button>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}
